import React, { useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Modale d'ajout / retrait d'un tag (groupe) en masse sur des contacts sélectionnés.
 * Liste les tags existants de l'agence + permet d'en créer un à la volée.
 */
export const BulkTagModal = ({ open, contactIds = [], existingTags = [], onClose, onDone }) => {
  const [tag, setTag] = useState('');
  const [action, setAction] = useState('add'); // 'add' | 'remove'
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    const value = (tag || '').trim();
    if (!value) {
      toast.error('Choisissez ou saisissez un tag');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/contacts/bulk-tag', {
        contact_ids: contactIds,
        tag: value,
        action,
      });
      toast.success(
        `${action === 'add' ? 'Tag ajouté à' : 'Tag retiré de'} ${data.modified_count} contact(s)`
      );
      setTag('');
      if (onDone) onDone(data);
    } catch (e) {
      console.error('Erreur bulk-tag:', e);
      toast.error('Erreur lors de la mise à jour du tag');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
      data-testid="bulk-tag-overlay"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-tag-modal"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            <i className="bi bi-tags-fill text-blue-600"></i>
            Tag en masse — {contactIds.length} contact(s)
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="bulk-tag-close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Action add / remove */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setAction('add')}
              className={`py-2.5 rounded-lg text-sm font-semibold border-2 transition flex items-center justify-center gap-2 ${
                action === 'add'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
              data-testid="bulk-tag-action-add"
            >
              <i className="bi bi-plus-circle"></i>Ajouter
            </button>
            <button
              type="button"
              onClick={() => setAction('remove')}
              className={`py-2.5 rounded-lg text-sm font-semibold border-2 transition flex items-center justify-center gap-2 ${
                action === 'remove'
                  ? 'border-red-400 bg-red-50 text-red-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
              data-testid="bulk-tag-action-remove"
            >
              <i className="bi bi-dash-circle"></i>Retirer
            </button>
          </div>

          {/* Saisie / création */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tag</label>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Ex. VIP, Acheteur chaud…"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="bulk-tag-input"
            />
          </div>

          {/* Tags existants */}
          {existingTags.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Tags existants
              </p>
              <div className="flex flex-wrap gap-1.5">
                {existingTags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTag(t)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition ${
                      tag === t
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                    data-testid={`bulk-tag-suggestion-${t}`}
                  >
                    <i className="bi bi-tag mr-1"></i>{t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-semibold"
            data-testid="bulk-tag-cancel"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className={`px-4 py-2 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 ${
              action === 'remove' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="bulk-tag-submit"
          >
            {busy ? <i className="bi bi-hourglass-split animate-spin"></i> : <i className="bi bi-check-lg"></i>}
            {action === 'add' ? 'Ajouter le tag' : 'Retirer le tag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkTagModal;
